/**
 * The role's brief on screen and its saves (Sofia, Oct 27 2026: one brief per role, edited by every
 * recruiter). Each save names the version on screen; this page's saves run one after the other, so
 * two quick changes never refuse each other. A colleague's save in between is refused (409
 * BRIEF_CHANGED): the toast gives the server's message and their version is shown.
 */
import { useCallback, useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { BriefDoc } from '../../types/taOutreach';

export function useSharedBrief(postingId: string, roleTitle: string | undefined) {
  const [brief, setBrief] = useState<BriefDoc | null>(null);
  /** The version on screen — what the next save is made on top of. */
  const version = useRef(0);
  const showBrief = useCallback((doc: BriefDoc | null) => {
    version.current = doc?.version ?? 0;
    setBrief(doc);
  }, []);
  const saves = useRef<Promise<void>>(Promise.resolve());
  /** Bumped when a colleague's version replaced ours: saves queued before it are dropped (CodeRabbit, PR #750). */
  const epoch = useRef(0);

  function saveBrief(patch: Parameters<typeof api.saveBrief>[1]) {
    const queuedAt = epoch.current;
    const run = async () => {
      // Queued on the version a colleague's save replaced: sent now it would overwrite their change unseen.
      if (queuedAt !== epoch.current) return;
      try {
        showBrief((await api.saveBrief(postingId, patch, version.current)).brief);
        if (patch.brief) void logAnalyticsEvent('ta_outreach_brief_edited', {});
      } catch (err) {
        showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save the brief.' });
        // Read by shape, not `instanceof` (see `briefSourceProblem`).
        if ((err as { body?: { code?: unknown } } | null)?.body?.code === 'BRIEF_CHANGED') {
          const r = await api.fetchOutreach(postingId, roleTitle).catch(() => null);
          if (r) {
            epoch.current += 1;
            showBrief(r.brief);
          }
        }
      }
    };
    const next = saves.current.then(run);
    saves.current = next;
    return next;
  }

  return { brief, showBrief, saveBrief };
}
